/**
 * Karbon360° v2 — current-session identity.
 *
 * Fetches /api/auth/me once per page load and shares the result, replacing the
 * three places that independently `atob`-decoded the JWT and the hardcoded
 * ADMIN_EMAILS list on the client.
 *
 * IMPORTANT: this is for RENDERING ONLY — deciding which menu items to show.
 * It is not a security boundary. Every admin route is enforced server-side by
 * requireAdmin/requireOwner reading users.role from the database, so hiding a
 * link here changes what is convenient, never what is permitted.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

let cache = null;

function claimsFromToken() {
  try {
    const t = localStorage.getItem('token');
    if (!t) return {};
    return JSON.parse(atob(t.split('.')[1])) || {};
  } catch {
    return {};
  }
}

/** Resolves to { id, username, email, role, subscription_plan, billing_plan } — role from the server. */
export function loadSession() {
  if (cache) return cache;
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve({});
  cache = axios
    .get(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => r.data || {})
    .catch(() => {
      cache = null; // let a later render retry rather than caching a failure
      return claimsFromToken();
    });
  return cache;
}

/** Clear on logout / account switch so the next load re-fetches. */
export function clearSession() {
  cache = null;
}

export function useSession() {
  const [session, setSession] = useState(() => claimsFromToken());
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let alive = true;
    loadSession().then((s) => {
      if (!alive) return;
      setSession((prev) => ({ ...prev, ...s }));
      setResolved(true);
    });
    return () => { alive = false; };
  }, []);

  const role = session.role || 'user';
  const isAdmin = role === 'owner' || role === 'admin';
  return {
    ...session,
    role,
    isAdmin,
    isOwner: role === 'owner',
    // CBAM entitlement — billing_plan from /api/auth/me, with admins and owners
    // always through so support can see what a customer sees. Rendering only:
    // every /api/cbam/* route re-checks the plan server-side (requireCompliance).
    hasCbam: isAdmin || ['compliance', 'enterprise'].includes(session.billing_plan),
    // The first render only has JWT claims — role and billing_plan arrive async.
    // Gated surfaces hide until this flips rather than flashing an upgrade wall.
    resolved,
  };
}
